import { useEffect } from "react"
import { create as createStore } from "zustand"

export type AsyncGlobalState<T> = {
  loading: boolean
  failed: boolean
  data: T
}

export function createAsyncGlobalState<T>({
  initialize,
  initialValue,
}: {
  initialize: () => Promise<T>
  initialValue: T
}) {
  const store = createStore<AsyncGlobalState<T>>(() => ({
    loading: true,
    failed: false,
    data: initialValue,
  }))

  let fetched = false
  let promise: Promise<T> | null = null

  const fetchData = async () => {
    try {
      if (store.getState().failed) {
        store.setState({ loading: true, failed: false, data: initialValue })
      }
      const data = await initialize()
      store.setState({ loading: false, failed: false, data })
      return data
    } catch (error) {
      store.setState({ loading: false, failed: true })
      console.error("Failed to initialize state:", error)
      return initialValue
    }
  }

  return {
    get: () => store.getState(),

    set: (data: T) => store.setState({ data }),

    use: () => {
      useEffect(() => {
        if (!fetched) {
          fetched = true
          store.setState({ loading: true })
          promise = fetchData()
        }
      }, [])

      return store()
    },

    promise: () => {
      if (!promise) {
        promise = fetchData()
      }
      return promise
    },

    reload: () => {
      promise = fetchData()
      return promise
    },
  }
}
